import React from "react";
import { Link } from "react-router-dom";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .blog-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .blog-root { font-family: var(--sans); background: var(--paper); color: var(--ink); min-height: 100vh; }

  .blog-header {
    border-bottom: 1px solid var(--rule);
    padding: 14px 0;
    background: var(--paper);
    position: sticky; top: 0; z-index: 50;
  }
  .blog-header-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .blog-header-pub {
    font-family: var(--serif); font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
  }
  .blog-header-tag {
    font-size: 11px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-muted); background: var(--paper-warm);
    border: 1px solid var(--rule); border-radius: 2px; padding: 4px 10px;
  }

  .blog-hero { max-width: 1200px; margin: 0 auto; padding: 64px 32px 0; }

  .blog-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .blog-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .blog-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent);
  }

  .blog-title {
    font-family: var(--serif); font-size: clamp(36px, 5.5vw, 72px);
    font-weight: 900; line-height: 1.05; letter-spacing: -0.02em;
    color: var(--ink); max-width: 900px;
  }
  .blog-title em { font-style: italic; color: var(--accent); }

  .blog-deck {
    margin-top: 24px; font-size: 18px; font-weight: 300; line-height: 1.65;
    color: var(--ink-muted); max-width: 600px; border-left: 2px solid var(--rule);
    padding-left: 20px;
  }

  .blog-hero-image {
    margin-top: 48px; position: relative; overflow: hidden;
    aspect-ratio: 16 / 7; background: var(--paper-warm);
  }
  .blog-hero-image img {
    width: 100%; height: 100%; object-fit: cover;
    display: block; transition: transform 8s ease;
  }
  .blog-hero-image:hover img { transform: scale(1.03); }
  .blog-hero-caption {
    position: absolute; bottom: 16px; right: 16px;
    font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(255,255,255,0.7); background: rgba(0,0,0,0.4);
    padding: 4px 10px; border-radius: 2px;
  }

  .blog-divider {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; gap: 24px; margin-top: 56px;
  }
  .blog-divider hr { flex: 1; border: none; border-top: 1px solid var(--rule); }
  .blog-divider-ornament { font-family: var(--serif); font-size: 20px; color: var(--ink-faint); }

  .blog-body {
    max-width: 1200px; margin: 0 auto; padding: 0 32px 96px;
    display: grid; grid-template-columns: 280px 1fr;
    gap: 0 80px; margin-top: 56px;
    align-items: start;
  }

  .blog-sidebar { position: sticky; top: 80px; }

  .blog-sidebar-lead {
    font-family: var(--serif); font-size: 22px; font-weight: 700;
    line-height: 1.35; letter-spacing: -0.01em; color: var(--ink);
    margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--rule);
  }

  .blog-meta-row {
    display: flex; flex-direction: column; gap: 14px; margin-bottom: 32px;
  }
  .blog-meta-item { font-size: 12px; color: var(--ink-muted); }
  .blog-meta-label {
    display: block; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; font-size: 10px; color: var(--ink-faint); margin-bottom: 3px;
  }

  .blog-toc { margin-top: 32px; }
  .blog-toc-title {
    font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-faint); margin-bottom: 14px;
  }
  .blog-toc-list { list-style: none; display: flex; flex-direction: column; gap: 8px; }
  .blog-toc-item a {
    font-size: 13px; color: var(--ink-muted); text-decoration: none;
    display: flex; align-items: center; gap: 8px;
    transition: color 0.15s;
  }
  .blog-toc-item a:hover { color: var(--accent); }
  .blog-toc-item a::before {
    content: ''; flex: 0 0 16px; height: 1px; background: currentColor; opacity: 0.4;
  }

  .blog-article { min-width: 0; }

  .blog-article h2 {
    font-family: var(--serif); font-size: 30px; font-weight: 700;
    line-height: 1.2; letter-spacing: -0.01em; color: var(--ink);
    margin-top: 56px; margin-bottom: 20px; padding-top: 56px;
    border-top: 1px solid var(--rule);
  }
  .blog-article h2:first-child { margin-top: 0; padding-top: 0; border-top: none; }

  .blog-article p {
    font-size: 17px; line-height: 1.78; color: #2a2724; margin-bottom: 20px;
  }

  .blog-article strong { font-weight: 600; color: var(--ink); }

  .blog-impact {
    font-family: var(--serif); font-size: 20px; font-style: italic;
    line-height: 1.5; color: var(--ink); background: var(--paper-warm);
    border-left: 3px solid var(--accent); padding: 20px 24px;
    margin: 28px 0;
  }

  .blog-article ul {
    margin: 0 0 20px 0; padding: 0; list-style: none;
    display: flex; flex-direction: column; gap: 8px;
  }
  .blog-article ul li {
    font-size: 16px; line-height: 1.6; color: #2a2724;
    display: flex; align-items: flex-start; gap: 12px;
  }
  .blog-article ul li::before {
    content: '\u2014'; color: var(--accent); font-weight: 700;
    flex-shrink: 0; margin-top: 1px;
  }

  .blog-article blockquote {
    margin: 40px 0; padding: 32px 40px;
    background: var(--ink); color: var(--paper);
    position: relative;
  }
  .blog-article blockquote::before {
    content: '\u201C';
    font-family: var(--serif); font-size: 80px; line-height: 0.8;
    color: var(--accent); position: absolute; top: 24px; left: 24px;
    opacity: 0.6;
  }
  .blog-article blockquote p {
    font-family: var(--serif); font-size: 21px; font-style: italic;
    line-height: 1.5; color: var(--paper); margin: 0; padding-left: 32px;
  }

  /* Numbered outcome cards */
  .blog-outcomes {
    display: flex; flex-direction: column;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule); margin: 36px 0;
  }
  .blog-outcome {
    background: var(--paper); padding: 28px 32px;
    display: grid; grid-template-columns: 48px 1fr;
    gap: 0 24px; align-items: start;
  }
  .blog-outcome:hover { background: var(--paper-warm); }
  .blog-outcome-num {
    font-family: var(--serif); font-size: 36px; font-weight: 900;
    color: var(--accent); line-height: 1; opacity: 0.35;
  }
  .blog-outcome-body {}
  .blog-outcome-title {
    font-family: var(--serif); font-size: 20px; font-weight: 700;
    color: var(--ink); margin-bottom: 8px; line-height: 1.2;
  }
  .blog-outcome-desc {
    font-size: 15px; line-height: 1.6; color: var(--ink-muted);
  }

  /* Closing statement */
  .blog-closing {
    margin-top: 56px; padding-top: 56px; border-top: 1px solid var(--rule);
    text-align: center;
  }
  .blog-closing-text {
    font-family: var(--serif); font-size: clamp(22px, 3vw, 32px);
    font-weight: 700; line-height: 1.25; color: var(--ink);
    max-width: 560px; margin: 0 auto;
  }
  .blog-closing-text em { font-style: italic; color: var(--accent); }
  .blog-closing-sub {
    margin-top: 20px; font-size: 15px; color: var(--ink-muted);
  }

  /* ── Prev / Next Nav ── */
  .blog-nav {
    border-top: 1px solid var(--rule);
    max-width: 1200px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
  }
  .blog-nav-cell {
    background: var(--paper); padding: 32px 40px;
    text-decoration: none; display: flex; flex-direction: column; gap: 8px;
    transition: background 0.15s;
  }
  .blog-nav-cell:hover { background: var(--paper-warm); }
  .blog-nav-cell.next { text-align: right; align-items: flex-end; }
  .blog-nav-cell.prev { align-items: flex-start; }
  .blog-nav-label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent);
    display: flex; align-items: center; gap: 8px;
  }
  .blog-nav-cell.next .blog-nav-label { flex-direction: row-reverse; }
  .blog-nav-label::before {
    content: ''; display: block; width: 20px; height: 1px; background: var(--accent);
  }
  .blog-nav-title {
    font-family: var(--serif); font-size: 18px; font-weight: 700;
    line-height: 1.25; color: var(--ink); max-width: 340px;
  }
  .blog-nav-arrow {
    font-size: 20px; color: var(--accent); margin-top: 4px;
    transition: transform 0.2s ease;
  }
  .blog-nav-cell:hover .blog-nav-arrow { transform: translateX(4px); }
  .blog-nav-cell.prev:hover .blog-nav-arrow { transform: translateX(-4px); }

  .blog-footer {
    border-top: 1px solid var(--rule); background: var(--paper-warm);
    padding: 40px 32px; text-align: center;
  }
  .blog-footer p { font-size: 13px; color: var(--ink-faint); }
  .blog-footer strong { color: var(--ink-muted); }

  @media (max-width: 900px) {
    .blog-body { grid-template-columns: 1fr; gap: 40px; }
    .blog-sidebar { position: static; }
    .blog-toc { display: none; }
    .blog-outcome { grid-template-columns: 36px 1fr; gap: 0 16px; padding: 20px; }
    .blog-hero { padding: 40px 20px 0; }
    .blog-body { padding: 0 20px 64px; margin-top: 40px; }
    .blog-divider { padding: 0 20px; }
  }
`;

export default function BlogPart3() {
  const outcomes = [
    {
      num: "1",
      title: "Charges Go Through Cleanly",
      desc: "Fewer questions. Less friction. No back-and-forth with customers over timeline discrepancies or data inconsistencies."
    },
    {
      num: "2",
      title: "Payments Become Predictable",
      desc: "Less variability in collection cycles. Fewer surprises at month end. The billing pattern becomes something you can actually plan around."
    },
    {
      num: "3",
      title: "More Revenue Gets Collected",
      desc: "Not because you bill more — but because what you bill holds up. Fewer partial payments, fewer write-offs, fewer disputes that quietly erode the final number."
    },
    {
      num: "4",
      title: "Less Time Defending Decisions",
      desc: "No chasing. No explaining. No reconciling charges weeks after the fact. The team spends time on operations, not on justifying what the system already did."
    },
  ];

  return (
    <>
      <style>{styles}</style>
      <div className="blog-root">

        

        <div className="blog-hero">
          <div className="blog-kicker">
            <span className="blog-kicker-line" />
            <span className="blog-kicker-text">Decision Integrity — Outcomes [Part III]</span>
          </div>

          <h1 className="blog-title">
            What Actually Changes When You Stop Sending <em>Weak Charges</em>
          </h1>

          <p className="blog-deck">
            Nothing dramatic happens overnight. But over time, a different pattern emerges — in revenue, in operations, and in how customers engage with your invoices.
          </p>

          <div className="blog-hero-image">
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1600&auto=format&fit=crop"
              alt="Business outcomes and revenue patterns"
            />
            <span className="blog-hero-caption">Revenue · Operations · Outcomes</span>
          </div>
        </div>

        <div className="blog-divider">
          <hr />
          <span className="blog-divider-ornament">§</span>
          <hr />
        </div>

        <div className="blog-body">

          <aside className="blog-sidebar">
            <p className="blog-sidebar-lead">
              Strong charges don't get challenged. They just get paid.
            </p>

            <div className="blog-meta-row">
              <div className="blog-meta-item">
                <span className="blog-meta-label">Series</span>
                Decision Integrity
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Reading Time</span>
                3 min
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Topic</span>
                Revenue · Collections · Operations
              </div>
            </div>

            <nav className="blog-toc">
              <p className="blog-toc-title">In this article</p>
              <ul className="blog-toc-list">
                {["The pattern that emerges", "Four things that shift", "Why it compounds", "The bottom line"].map(t => (
                  <li key={t} className="blog-toc-item"><a href="#">{t}</a></li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="blog-article">

            <h2>The pattern that emerges</h2>
            <p>
              Nothing dramatic changes overnight. The first invoice goes out. It gets paid.
              The second invoice goes out. It gets paid. No emails. No disputes. No partial acceptance.
            </p>
            <p>
              At first it feels like coincidence. Then it becomes the norm.
            </p>
            <p className="blog-impact">
              When every charge is defensible before it's sent, the dispute never starts.
            </p>

            <h2>Four things that shift</h2>
            <p>
              The changes aren't dramatic individually. Together they reshape how billing actually works:
            </p>

            <div className="blog-outcomes">
              {outcomes.map(o => (
                <div className="blog-outcome" key={o.num}>
                  <div className="blog-outcome-num">{o.num}</div>
                  <div className="blog-outcome-body">
                    <div className="blog-outcome-title">{o.title}</div>
                    <div className="blog-outcome-desc">{o.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <h2>Why it compounds</h2>
            <p>
              Each clean billing cycle reinforces the next. Customers stop scrutinising invoices
              as closely when they've never had reason to push back. The relationship shifts from
              adversarial to routine.
            </p>
            <p>
              Internally, the team stops building habits around dispute resolution — and starts
              building habits around execution. The operational overhead that was quietly absorbed
              every month simply disappears.
            </p>

            <blockquote>
              <p>
                You don't notice the cost of weak charges until they're gone. Then the difference is obvious.
              </p>
            </blockquote>

            <h2>The bottom line</h2>
            <p>
              This isn't about billing more aggressively or chasing more revenue.
              It's about making sure the revenue you've already earned actually lands — cleanly,
              on time, without friction.
            </p>
            <p>
              The charges were always valid. The issue was the moment they were sent.
              Fix the timing, and the rest follows.
            </p>

            <div className="blog-closing">
              <p className="blog-closing-text">
                Strong charges don't get challenged.<br />
                They just get <em>paid.</em>
              </p>
              
            </div>

          </article>
        </div>

        <nav className="blog-nav">
          <Link to="/blogs/2" className="blog-nav-cell prev">
            <span className="blog-nav-label">Previous</span>
            <span className="blog-nav-title">Part II: What Changed When the System Started Enforcing It</span>
            <span className="blog-nav-arrow">←</span>
          </Link>
          <div style={{ background: "var(--paper-warm)", padding: "32px 40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "13px", color: "var(--ink-faint)" }}>End of series</span>
          </div>
        </nav>

        <footer className="blog-footer">
        <p><strong style={{ color: "var(--ink-muted)" }}>Documore LLC</strong> — © {new Date().getFullYear()}</p>
        </footer>

      </div>
    </>
  );
}
